import { Modal, Table } from "antd";
import styles from "./paymentPreviewModal.module.scss";
import Button from "../../custom/button/button";
import { capitalizeString } from "../../utils/stringHelpers";
import { useAtomValue } from "jotai";
import { tempNominees, userData } from "../../state";
import { MemberType } from "../../requests";
import { getDateWithoutTime } from "../../utils/formatDate";

interface PreviewProps {
  open: boolean;
  onClose: () => void;
  submitForm: () => void;
  eventType: "Training" | "Conference" | "Event";
  memberType: "corporate" | "individual";
  eventDetails: {
    eventTheme: string;
    eventName: string;
    location: string;
    organizationName?: string;
    organizationAddress?: string;
    organizationContactName?: string;
    organizationContactEmail?: string;
    organizationContactPhone?: string;
  };
  allSessions?: Session[];
  selectedSessions?: string[];
  schedule?: EventSchedule;
}

const columns = [
  {
    title: "Name",
    dataIndex: "ParticipantName",
    key: "ParticipantName",
  },
  {
    title: "Phone Number",
    dataIndex: "ParticipantPhoneNo",
    key: "ParticipantPhoneNo",
  },
  {
    title: "Email Address",
    dataIndex: "ParticipantEmail",
    key: "ParticipantEmail",
  },
];

const PaymentPreviewModal: React.FC<PreviewProps> = ({
  open,
  onClose,
  submitForm,
  eventType,
  memberType,
  eventDetails,
  allSessions,
  selectedSessions = [],
  schedule = {} as EventSchedule,
}) => {
  const user = useAtomValue(userData);
  const isCorporate = user?.MembershipTypeId === MemberType.Corporate;
  const attendees = useAtomValue(tempNominees);

  const getSessionName = (sessionId: number) =>
    allSessions?.find((x) => x.Id === sessionId)?.SessionName;
  const sessionNames = selectedSessions
    ? selectedSessions?.map((x: string) => getSessionName(Number(x)))
    : [];

  return (
    <Modal
      centered
      title="Preview Registration Details"
      open={open}
      onCancel={onClose}
      footer={null}
    >
      <div className="colGap">
        <section className={styles.preview}>
          <div>
            <h4>{eventType} Theme</h4>
            <p>{eventDetails?.eventTheme}</p>
          </div>
          <div>
            <h4>{eventType} Type</h4>
            <p>{eventDetails?.eventName}</p>
          </div>
          {!isCorporate && (
            <div>
              <h4>Mode of sponsorship</h4>
              <p>{capitalizeString(memberType)}</p>
            </div>
          )}
          <div>
            <h4>Your location</h4>
            <p>{eventDetails?.location}</p>
          </div>
        </section>
        {memberType === "corporate" && (
          <div>
            <h3>Organization Details</h3>
            <section className={styles.preview}>
              <div>
                <h4>Organization name</h4>
                <p>{eventDetails?.organizationName}</p>
              </div>
              <div>
                <h4>Organization address</h4>
                <p>{eventDetails?.organizationAddress}</p>
              </div>
              <div>
                <h4>Contact person name</h4>
                <p>{eventDetails?.organizationContactName}</p>
              </div>
              <div>
                <h4>Contact person email</h4>
                <p>{eventDetails?.organizationContactEmail}</p>
              </div>
              <div>
                <h4>Contact person phone number</h4>
                <p>{eventDetails?.organizationContactPhone}</p>
              </div>
            </section>
          </div>
        )}
        {sessionNames && sessionNames?.length > 0 && (
          <div>
            <h3>Technical Sessions</h3>
            {sessionNames?.map((item, index) => (
              <p key={index}>
                <b>Session {index + 1}:</b> {item}
              </p>
            ))}
          </div>
        )}
        {schedule?.LearningEventId && (
          <div>
            <h3>Selected Schedule</h3>
            <p>
              <b>Date: </b>
              {getDateWithoutTime(schedule?.StartDate)} -{" "}
              {getDateWithoutTime(schedule?.EndDate)}
            </p>
            <p>
              <b>Time:</b> {schedule?.Time}
            </p>
          </div>
        )}
        {attendees?.length > 0 && (
          <div>
            <h3>Selected Attendees</h3>
            <Table
              columns={columns}
              dataSource={attendees}
              pagination={false}
              rowKey={(item) => item.ParticipantEmail}
              scroll={{ x: true }}
              size="small"
            />
          </div>
        )}
        <div className="ctaGroup">
          <Button className={styles.greyBtn} onClick={onClose} text="Cancel" />
          <Button
            onClick={() => {
              onClose();
              submitForm();
            }}
            text="Proceed to Payment"
          />
        </div>
      </div>
    </Modal>
  );
};

export default PaymentPreviewModal;
